import {motion, useTransform, useScroll, AnimatePresence} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import {useState, useRef, useEffect} from 'react';
import Lottie from 'lottie-web';

import FluidVideo from '../media/fluidVideo.client';
import VideoPopup from '../ui/VideoPopup.client';

import {pathVars} from '../ui/animationVars';

type Props = {
  backgroundImage: any;
  heading: {
    prefix: string;
    underlined: string;
    suffix: string;
  };
  videoUrl: string;
  leftLottie: any;
  rightLottie: any;
  bottomLottie: any;
  thumbnail: any;
};

export default function Banner({
  backgroundImage,
  heading,
  videoUrl,
  rightLottie,
  bottomLottie,
  thumbnail,
}: Props) {
  const {ref, inView} = useInView({
    threshold: 0.7,
    triggerOnce: true,
  });

  const {scrollY} = useScroll();

  const y = useTransform(
    scrollY,
    [0, 2000], // px to scroll
    [-100, 1000], // y change
  );

  // Lotties
  const rightRef = useRef<any>();
  const bottomRef = useRef<any>();

  useEffect(() => {
    const animRight = Lottie.loadAnimation({
      container: rightRef.current, // the dom element that will contain the animation
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: rightLottie?.asset?.url, // the path to the animation json
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    });
    const animBottom = Lottie.loadAnimation({
      container: bottomRef.current, // the dom element that will contain the animation
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: bottomLottie?.asset?.url, // the path to the animation json
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    });

    animRight.play();
    animBottom.play();

    return () => {
      animRight.destroy();
      animBottom.destroy();
    };
  });

  const [isOpen, setIsOpen] = useState<any>(false);

  return (
    <>
      <div className="relative grid min-h-screen grid-cols-14 bg-light-beige">
        {/* Image */}
        <div className="pointer-events-none col-start-1 col-end-15 h-screen lg:h-auto">
          <motion.img
            alt={heading?.underlined}
            style={{y}}
            src={backgroundImage?.url}
            className="absolute top-0 left-0 h-full w-full object-cover lg:relative"
          />
        </div>
        {/* Text */}
        <div
          ref={ref}
          className="absolute top-space-l col-start-2 col-end-14 w-full text-center md:col-start-3 md:col-end-13"
        >
          <h1 className="text-[38px] font-bold md:text-[64px] xl:text-[80px]">
            {heading?.prefix}&nbsp;
            <div className="relative inline-block w-max">
              <span className="relative z-10">{heading?.underlined}</span>
              <svg
                className="absolute bottom-1 w-full"
                width="616"
                height="17"
                viewBox="0 0 616 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <motion.path
                  variants={pathVars}
                  initial="initial"
                  animate={inView ? 'animate' : 'initial'}
                  d="M3 8.86221C205.004 3.71797 413.196 -3.29995 613 14"
                  stroke="#F27021"
                  strokeWidth="10"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            &nbsp;{heading?.suffix}
          </h1>
        </div>
        {/* Video */}
        <div className="relative z-30 col-start-1 col-end-15 grid w-full grid-cols-14 bg-light-beige py-24 lg:py-40">
          <div className="absolute -top-1/4 z-20 col-start-2 col-end-14 w-full overflow-hidden rounded-lg bg-black md:top-[unset] md:bottom-0 md:col-start-3 md:col-end-13">
            <FluidVideo
              url={videoUrl}
              playing
              playsinline
              muted
              loop
              light={thumbnail?.url}
            />
            <div
              onClick={() => setIsOpen(videoUrl)}
              className="absolute top-0 left-0 h-full w-full cursor-pointer"
            />
          </div>
          {/* Birds */}
          <div
            ref={rightRef}
            className="pointer-events-none absolute right-0 -bottom-1/3 left-0 z-20 w-full -translate-x-8 md:-bottom-full md:translate-x-0"
          />
          <div
            ref={bottomRef}
            className="pointer-events-none absolute bottom-full left-0 z-20 w-full overflow-visible md:h-[600px] xl:bottom-[160%] 2xl:bottom-[240%] 2xl:h-[1000px] 3xl:bottom-[300%]"
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isOpen && <VideoPopup url={isOpen} setIsOpen={setIsOpen} />}
      </AnimatePresence>
    </>
  );
}
